import { RECAPTCHA_ENDPOINTS, RECAPTCHA_CONFIG } from "@/constants/recaptcha.constants";
import {
  RecaptchaVerifyRequest,
  RecaptchaVerifyResponse,
  RecaptchaTokenVerifier,
  RecaptchaConfigValidator,
  RecaptchaErrorHandler as IRecaptchaErrorHandler,
} from "@/interfaces/recaptcha.interfaces";
import { apiT2B } from "@/lib/axios";

/**
 * Manipulador de erro simples para o serviço
 */
class SimpleRecaptchaErrorHandler implements IRecaptchaErrorHandler {
  handleError(error: unknown): void {
    throw error;
  }

  getErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : "Unknown error";
  }
}

/**
 * Serviço para verificar tokens reCAPTCHA através da API backend
 * Implementa Single Responsibility Principle e Dependency Inversion
 */
export class RecaptchaService implements RecaptchaTokenVerifier, RecaptchaConfigValidator {
  private static instance: RecaptchaService;
  private readonly errorHandler: IRecaptchaErrorHandler;

  private constructor() {
    this.errorHandler = new SimpleRecaptchaErrorHandler();
  }

  public static getInstance(): RecaptchaService {
    if (!RecaptchaService.instance) {
      RecaptchaService.instance = new RecaptchaService();
    }
    return RecaptchaService.instance;
  }

  public async verifyToken(recaptchaToken: string): Promise<boolean> {
    try {
      const requestPayload = this.buildVerifyRequest(recaptchaToken);
      const response = await this.makeVerificationRequest(requestPayload);

      return this.isVerificationSuccessful(response);
    } catch (error) {
      this.errorHandler.handleError(error);
      return false;
    }
  }

  public isSiteKeyConfigured(): boolean {
    return Boolean(RECAPTCHA_CONFIG.SITE_KEY && RECAPTCHA_CONFIG.SITE_KEY.trim() !== "");
  }

  public isSecretKeyConfigured(): boolean {
    return Boolean(RECAPTCHA_CONFIG.SECRET_KEY && RECAPTCHA_CONFIG.SECRET_KEY.trim() !== "");
  }

  public validateConfiguration(): boolean {
    return this.isSiteKeyConfigured() && this.isSecretKeyConfigured();
  }

  private buildVerifyRequest(recaptchaToken: string): RecaptchaVerifyRequest {
    // Enviar apenas o campo padronizado esperado pelo backend
    return {
      gRecaptchaToken: recaptchaToken,
    } as RecaptchaVerifyRequest;
  }

  private async makeVerificationRequest(
    requestPayload: RecaptchaVerifyRequest
  ): Promise<RecaptchaVerifyResponse> {
    const response = await apiT2B.post<RecaptchaVerifyResponse>(
      RECAPTCHA_ENDPOINTS.API_VERIFY,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  }

  private isVerificationSuccessful(response: RecaptchaVerifyResponse): boolean {
    return response?.success === true;
  }
}

export const recaptchaService = RecaptchaService.getInstance();
